function envValue(key: string) {
  if (typeof process !== "undefined" && process.env?.[key]) return process.env[key]
  // In the browser bundle only VITE_-prefixed variables exist on
  // import.meta.env, so PUBLIC_STAGE resolves through its VITE_ mirror there —
  // without it, every stage-derived URL silently fell to its dev default
  // client-side (caught live: the sidebar's short-links link opened dev).
  return import.meta.env?.[key] ?? import.meta.env?.[`VITE_${key}`]
}

export { createTtlCache, type TtlCache, type TtlCacheOptions } from "./cache"
export {
  type DateInput,
  unixMsDate,
  utcDate,
  utcDateOnly,
  utcDayjs,
  utcIsoString,
  utcNow,
  utcObjectKeyTimestamp,
  utcPathParts,
  utcUnixMs,
} from "./date"
export { type LogContext, type LogLevel, logEvent } from "./logger"

export function isDevStage(stage = envValue("PUBLIC_STAGE")) {
  return !stage || stage === "dev" || stage === "local"
}

export function dashboardDomain(stage = envValue("PUBLIC_STAGE")) {
  if (stage === "dev") return "dev.dashboard.playbrain.com"
  if (stage === "staging") return "staging.dashboard.playbrain.com"
  if (stage === "prod") return "dashboard.playbrain.com"
  return "localhost:3000"
}

/**
 * Taiyoro's dashboard host for the current stage, so the launcher card does not
 * send someone working on dev across to production.
 */
export function taiyoroDashboardUrl(stage = envValue("PUBLIC_STAGE")) {
  if (stage === "prod") return "https://dashboard.taiyoro.gg/"
  if (stage === "staging") return "https://staging.dashboard.taiyoro.gg/"
  return "https://dev.dashboard.taiyoro.gg/"
}

/**
 * The short-links (Kutt) UI host for the current stage, so the dashboard link
 * sends someone working on staging to staging rather than production.
 */
export function shortLinksUrl(stage = envValue("PUBLIC_STAGE")) {
  if (stage === "prod") return "https://links.playbrain.com/"
  if (stage === "staging") return "https://staging.links.playbrain.com/"
  return "https://dev.links.playbrain.com/"
}

/**
 * Whether the short-links app is actually deployed for the stage. Staging and
 * prod run the service; dev has none, and best-effort calls to its host — the
 * front-channel logout ping — should not fire at a host that does not exist.
 */
export function shortLinksDeployed(stage = envValue("PUBLIC_STAGE")) {
  return stage === "staging" || stage === "prod"
}

export function dashboardUrl(stage = envValue("PUBLIC_STAGE")) {
  const domain = dashboardDomain(stage)
  return domain.startsWith("localhost") ? `http://${domain}/dashboard` : `https://${domain}`
}

export function dashboardLoginUrl(stage = envValue("PUBLIC_STAGE")) {
  const domain = dashboardDomain(stage)
  return domain.startsWith("localhost") ? `http://${domain}/login` : `https://${domain}/login`
}
